/**
 * ThreadHeaderActionButtons — pin + archive for the ACTIVE thread in the chat
 * header's top-right action rail (sidebar/header UX round 3, 2026-08-01).
 *
 * Two invariant HeaderIconButtons, styled exactly like their rail siblings
 * (add-people / mode-cycle / hide):
 *   • PIN — toggles the active thread's pinned state through the SAME
 *     `threadStore.togglePinThread` the row/chip pin affordances call (one
 *     mutation path; pins persist on the thread index doc and sync across
 *     devices). Filled thumbtack + aria-pressed while pinned.
 *   • ARCHIVE — requests archive of the active thread through the SAME
 *     confirm/delete flow the row × uses (the host routes it via
 *     `shouldRouteCloseToEmptyDelete` → one-click empty delete or the
 *     ConfirmationModal → `removeThreadAndReturnFallback`, which lands the
 *     user on the fallback thread). Hidden while the thread is the last one
 *     (`canArchive` — the row ×'s own `canDelete` rule).
 *
 * Presentational: no thread-store access, no routing decisions — the host
 * owns both, so this rail can never diverge from the row affordances.
 * `data-header-pin-btn` / `data-header-archive-btn` are the stable hooks.
 */
import React from 'react';
import { HeaderIconButton, ThreadUnreadBadge } from './styles';
import { PinSvgIcon, ArchiveBoxSvgIcon } from './components';
import type { ThreadNotifyMode } from '../../services/threadNotifications';
import { CategoryMoveButton, type CategoryMoveProps } from './CategoryMoveMenu';

/**
 * Bell tooltip suffix when the OS/browser has notifications BLOCKED for this
 * site (platform-aware permission, 2026-09-08): the 'chime-platform' state
 * cannot deliver, and saying so here is the one place the user is looking.
 * UI chrome copy (D9 — status, never content); points at the Settings card.
 */
export const OS_NOTIFICATIONS_BLOCKED_HINT =
  'os notifications are blocked for prest0 in your browser or device settings — fix it under settings → notifications';

/** Bell glyphs for the three notification states (owner directive 2026-08-26). */
const BellChimeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
  </svg>
);

const BellPlatformIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
    {/* ring waves — the "and a platform message" state */}
    <path d="M2.5 8a9.5 9.5 0 0 1 2-5"></path>
    <path d="M21.5 8a9.5 9.5 0 0 0-2-5"></path>
  </svg>
);

const BellOffIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8.7 3A6 6 0 0 1 18 8c0 4.5 1.2 6.9 2.2 8.2"></path>
    <path d="M17 17H3s3-2 3-9a6 6 0 0 1 .3-1.9"></path>
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
    <line x1="2" y1="2" x2="22" y2="22"></line>
  </svg>
);

const NOTIFY_MODE_PRESENTATION: Record<ThreadNotifyMode, { icon: React.ReactNode; title: string }> = {
  chime: {
    icon: <BellChimeIcon />,
    title: 'notifications: quick chime on new messages — click for chime + platform notifications',
  },
  'chime-platform': {
    icon: <BellPlatformIcon />,
    title: 'notifications: chime + platform notification per new message — click to turn notifications off',
  },
  off: {
    icon: <BellOffIcon />,
    title: 'notifications: off for this thread — click for the quick chime',
  },
};

export interface ThreadHeaderActionButtonsProps {
  /** Active thread id — both callbacks receive it. */
  threadId: string;
  /** Display name for aria-labels (routine name / thread title). */
  threadTitle: string;
  /** Current pinned state of the active thread. */
  pinned: boolean;
  /** The SAME togglePinThread the row/chip pin buttons call. */
  onTogglePin: (threadId: string) => void;
  /** Row-×-parity: false (last remaining thread) hides the archive button. */
  canArchive: boolean;
  /** Host routes into the row ×'s confirm/empty-delete flow. */
  onRequestArchive: (threadId: string) => void;
  /**
   * This VIEWER's notification mode for the active thread (owner directive
   * 2026-08-26) — absent hides the bell (host still resolving).
   */
  notifyMode?: ThreadNotifyMode;
  /** Advance the three-state cycle: chime → chime+platform → off → chime. */
  onCycleNotifyMode?: (threadId: string) => void;
  /**
   * Notification.permission === 'denied' for this site (2026-09-08): the bell
   * tooltip says so — the platform state cannot deliver until the user
   * un-blocks it. Never disables the control.
   */
  osNotificationsBlocked?: boolean;
  /**
   * Per-thread badges (2026-09-08): OTHER threads carry unseen activity —
   * never the open one. Renders a small dot on the bell.
   */
  otherThreadsUnseen?: boolean;
  /**
   * PERSONAL ORGANIZATION (PR-5, 2026-09-09): the folder button — file this
   * thread under one of the viewer's categories. Absent → no button.
   */
  moveTo?: CategoryMoveProps;
}

export const ThreadHeaderActionButtons: React.FC<ThreadHeaderActionButtonsProps> = ({
  threadId,
  threadTitle,
  pinned,
  onTogglePin,
  canArchive,
  onRequestArchive,
  notifyMode,
  onCycleNotifyMode,
  osNotificationsBlocked,
  otherThreadsUnseen,
  moveTo,
}) => {
  const notifyTitle = notifyMode
    ? `${NOTIFY_MODE_PRESENTATION[notifyMode].title}${osNotificationsBlocked ? ` · ${OS_NOTIFICATIONS_BLOCKED_HINT}` : ''}`
    : '';
  return (
  <>
    {notifyMode && onCycleNotifyMode && (
      <HeaderIconButton
        data-header-notify-btn=""
        data-notify-mode={notifyMode}
        data-os-notifications-blocked={osNotificationsBlocked ? 'true' : undefined}
        data-other-threads-unseen={otherThreadsUnseen ? 'true' : undefined}
        aria-label={`${notifyTitle} — thread ${threadTitle}${otherThreadsUnseen ? ' — other threads have new activity' : ''}`}
        title={notifyTitle}
        onClick={() => onCycleNotifyMode(threadId)}
      >
        {NOTIFY_MODE_PRESENTATION[notifyMode].icon}
        {otherThreadsUnseen && (
          <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="corner" />
        )}
      </HeaderIconButton>
    )}
    <HeaderIconButton
      data-header-pin-btn=""
      data-pinned={pinned ? 'true' : 'false'}
      aria-pressed={pinned}
      aria-label={pinned ? `unpin thread ${threadTitle}` : `pin thread ${threadTitle} to the top`}
      title={pinned ? 'unpin thread' : 'pin thread to top'}
      onClick={() => onTogglePin(threadId)}
    >
      <PinSvgIcon filled={pinned} />
    </HeaderIconButton>
    {moveTo && <CategoryMoveButton threadTitle={threadTitle} {...moveTo} />}
    {canArchive && (
      <HeaderIconButton
        data-header-archive-btn=""
        aria-label={`archive thread ${threadTitle} (Prest0n keeps it in memory for reference; the conversation closes)`}
        title="archive thread"
        onClick={() => onRequestArchive(threadId)}
      >
        <ArchiveBoxSvgIcon />
      </HeaderIconButton>
    )}
  </>
  );
};

export default ThreadHeaderActionButtons;
